/**
 * <slideshow-video> — lazy, accessibility-aware background video for slideshow slides.
 *
 * Performance: no video bytes are requested until the slide is close to/in the viewport
 * (IntersectionObserver) — the poster image alone paints the slide, so LCP is an image.
 * Accessibility: honours prefers-reduced-motion (poster stays, nothing autoplays) and
 * always exposes a play/pause toggle so moving content can be stopped (WCAG 2.2.2).
 */
if (!customElements.get('slideshow-video')) {
  customElements.define(
    'slideshow-video',
    class SlideshowVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('.slideshow__video-toggle');
        if (!this.video) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplay = this.dataset.autoplay !== 'false';

        if (this.toggle) {
          this.toggle.addEventListener('click', () => this.togglePlay());
          this.toggle.hidden = false;
        }

        // Pause when scrolled away, load + play when in view.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isInView = entry.isIntersecting;
              if (entry.isIntersecting) {
                this.load();
                // While load() is waiting for the page to finish loading, it starts playback itself.
                if (this.loaded && this.autoplay && !this.reduceMotion.matches && !this.userPaused) this.play();
              } else if (!this.video.paused) {
                this.video.pause();
                this.syncToggle();
              }
            });
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);

        this.video.addEventListener('play', () => this.syncToggle());
        this.video.addEventListener('pause', () => this.syncToggle());
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      /**
       * Swap data-src → src once, so the browser only now fetches the media.
       *
       * Performance: the hero video is several MB and used to start downloading as soon as the
       * page was parsed, competing with the poster image (the LCP element) and everything else.
       * Unless `immediate` (the visitor pressed play), it now waits for the window load event,
       * with a 4s fallback so a slow third-party script can't hold it back indefinitely.
       * The same video file is used; only the start time changes. The poster stays visible until
       * frames exist (video is opacity: 0 until --loaded), so nothing looks different meanwhile.
       */
      load(immediate = false) {
        if (this.loaded) return;
        if (!immediate && document.readyState !== 'complete') {
          if (this.waitingForPageLoad) return;
          this.waitingForPageLoad = true;
          const resume = () => {
            if (!this.waitingForPageLoad) return;
            this.waitingForPageLoad = false;
            this.load(true);
            if (this.isInView && this.autoplay && !this.reduceMotion.matches && !this.userPaused) this.play();
          };
          window.addEventListener('load', resume, { once: true });
          setTimeout(resume, 4000);
          return;
        }
        this.waitingForPageLoad = false;
        this.loaded = true;
        this.video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        this.video.load();
        this.video.addEventListener(
          'loadeddata',
          () => this.classList.add('slideshow__video-wrapper--loaded'),
          { once: true }
        );
      }

      play() {
        const playing = this.video.play();
        if (playing && typeof playing.catch === 'function') playing.catch(() => {});
      }

      togglePlay() {
        this.load(true);
        if (this.video.paused) {
          this.userPaused = false;
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      syncToggle() {
        if (!this.toggle) return;
        const paused = this.video.paused;
        this.toggle.classList.toggle('slideshow__video-toggle--paused', paused);
        this.toggle.setAttribute('aria-pressed', paused ? 'false' : 'true');
      }
    }
  );
}
